import { getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import api from '@/shared/services/all_bright_finder';
import { handleError } from '@/shared/services/api';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';

function resolveResourceBinding(resource) {
  if (resource === undefined) {
    return undefined;
  }
  if (typeof resource === 'function') {
    return resource(api);
  }
  return resource;
}

export default function useApi({
  fallbackRoute,
  processing,
  resource: resourceBinding,
  routeIdParam,
  syncLoadIndexOnMount,
  syncLoadWithRouteId,
  target: targetBinding,
} = {}) {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const eventBus = useEventBus();
  const { isSpecialist, isManager, isParent } = useRole();
  const boundResource = resolveResourceBinding(resourceBinding);
  const boundTarget = targetBinding;
  const boundFallbackRoute = fallbackRoute;
  const indexLoading = syncLoadIndexOnMount ? ref(true) : null;
  let hasSettledInitialIndexLoad = false;

  function resolveId(idFromCall) {
    if (idFromCall !== undefined && idFromCall !== null && idFromCall !== '') {
      return idFromCall;
    }
    if (!routeIdParam) {
      return undefined;
    }
    const fromRoute = route.params[routeIdParam];
    if (fromRoute === undefined || fromRoute === null || fromRoute === '') {
      return undefined;
    }
    if (Array.isArray(fromRoute)) {
      return fromRoute[0];
    }
    return fromRoute;
  }

  async function withProcessingWhenRef(run) {
    if (processing) {
      processing.value = true;
    }
    try {
      return await run();
    } finally {
      if (processing) {
        processing.value = false;
      }
    }
  }

  const getScopedApi = () => {
    if (!store?.state?.role) return null;

    if (isManager) return api.manager;
    if (isSpecialist) return api.organization;
    if (isParent) return api.parent;

    throw new Error(`Invalid role for scoped API: ${store.state.role}`);
  };

  const scopedApi = getScopedApi();

  async function loadResourceIndex(overrides = {}) {
    const hasKey = (key) => Object.hasOwn(overrides, key);
    const resource = hasKey('resource')
      ? resolveResourceBinding(overrides.resource)
      : boundResource;
    const target = hasKey('target') ? overrides.target : boundTarget;
    const params = hasKey('params') ? overrides.params : {};
    const afterResponse = hasKey('afterResponse') ? overrides.afterResponse : null;
    const useProcessing = hasKey('useProcessing') ? overrides.useProcessing : true;

    if (!resource) {
      throw new Error(
        'loadResourceIndex requires `resource` or useApi({ resource: ... }) or loadResourceIndex({ resource, ... })',
      );
    }
    if (!target) {
      throw new Error(
        'loadResourceIndex requires `target` or useApi({ target: ... }) or loadResourceIndex({ target, ... })',
      );
    }

    async function runLoadResourceIndex() {
      try {
        const response = await resource.index(params, null, (error) => handleError(error));
        target.value = response?.data ?? [];
        if (typeof afterResponse === 'function') {
          afterResponse(response);
        }
      } finally {
        if (indexLoading && !hasSettledInitialIndexLoad) {
          hasSettledInitialIndexLoad = true;
          indexLoading.value = false;
        }
      }
    }

    if (processing && useProcessing) {
      return withProcessingWhenRef(runLoadResourceIndex);
    }

    return runLoadResourceIndex();
  }

  async function createResource(
    createObject,
    { createdMessage = 'Created', resource: resourceFromCall } = {},
  ) {
    const resource = resourceFromCall ?? boundResource;
    if (!resource) {
      throw new Error(
        'createResource requires `resource` or useApi({ resource: ... }) or createResource(..., { resource: ... })',
      );
    }

    return withProcessingWhenRef(async () => {
      const response = await resource.create(createObject, null, (error) => handleError(error));

      if (response?.data) {
        eventBus.chime(createdMessage);
        const shouldReloadIndex =
          boundTarget && (syncLoadIndexOnMount || Array.isArray(boundTarget.value));
        if (shouldReloadIndex) {
          await loadResourceIndex({ useProcessing: false });
        }
        return response.data;
      }

      return null;
    });
  }

  async function loadResource({
    id: idFromCall,
    notFoundMessage = 'Resource could not be found.',
    params = {},
    resource: resourceFromCall,
    target: targetFromCall,
  } = {}) {
    const id = resolveId(idFromCall);
    const resource = resourceFromCall ?? boundResource;
    const target = targetFromCall ?? boundTarget;
    if (!resource) {
      throw new Error('loadResource requires `resource` or useApi({ resource: ... })');
    }
    if (!target) {
      throw new Error('loadResource requires `target` or useApi({ target: ... })');
    }
    if (id === undefined) {
      throw new Error(
        'loadResource requires `id` or useApi({ routeIdParam: ... }) with a matching route param',
      );
    }

    return withProcessingWhenRef(async () => {
      let notFound = false;
      const response = await resource.get(id, params, null, (error) => {
        if (error?.response?.status === 404) {
          notFound = true;
          eventBus.chime(notFoundMessage);
          return null;
        }
        return handleError(error);
      });

      if (response?.data) {
        target.value = response.data;
        return response.data;
      }

      if (notFound && boundFallbackRoute) {
        await router.push(boundFallbackRoute);
      }
      return null;
    });
  }

  function pickUpdatePayload(object, fieldKeys) {
    if (!fieldKeys || fieldKeys.length === 0) {
      return object;
    }
    const result = {};
    for (const key of fieldKeys) {
      if (Object.prototype.hasOwnProperty.call(object, key)) {
        result[key] = object[key];
      }
    }
    return result;
  }

  async function updateResource(firstArg, secondArg) {
    const isOptionsShape =
      firstArg !== null && typeof firstArg === 'object' && Object.hasOwn(firstArg, 'updateObject');

    const {
      id: idFromCall,
      notFoundMessage = 'Resource could not be found.',
      params = {},
      resource: resourceFromCall,
      savedMessage = 'Saved',
      target: targetFromCall,
      updateFieldKeys,
      updateObject,
    } = isOptionsShape ? firstArg : { updateObject: firstArg, updateFieldKeys: secondArg };

    const id = resolveId(idFromCall);
    const resource = resourceFromCall ?? boundResource;
    const target = targetFromCall ?? boundTarget;
    if (!resource) {
      throw new Error('updateResource requires `resource` or useApi({ resource: ... })');
    }
    if (!target) {
      throw new Error('updateResource requires `target` or useApi({ target: ... })');
    }
    if (id === undefined) {
      throw new Error(
        'updateResource requires `id` or useApi({ routeIdParam: ... }) with a matching route param',
      );
    }

    return withProcessingWhenRef(async () => {
      const payload = pickUpdatePayload(updateObject, updateFieldKeys);
      const response = await resource.update(id, payload, params, null, (error) => {
        if (error?.response?.status === 404) {
          eventBus.chime(notFoundMessage);
          return null;
        }
        return handleError(error);
      });

      if (response?.data) {
        target.value = response.data;
        eventBus.chime(savedMessage);
        return response.data;
      }

      return null;
    });
  }

  async function destroyResource({
    deletedMessage = 'Deleted',
    id: idFromCall,
    notFoundMessage = 'Resource could not be found.',
    onSuccess = null,
    resource: resourceFromCall,
  } = {}) {
    const id = resolveId(idFromCall);
    const resource = resourceFromCall ?? boundResource;
    if (!resource) {
      throw new Error('destroyResource requires `resource` or useApi({ resource: ... })');
    }
    if (id === undefined) {
      throw new Error(
        'destroyResource requires `id` or useApi({ routeIdParam: ... }) with a matching route param',
      );
    }

    return withProcessingWhenRef(async () => {
      const response = await resource.destroy(id, null, (error) => {
        if (error?.response?.status === 404) {
          eventBus.chime(notFoundMessage);
          return null;
        }
        return handleError(error);
      });

      if (!response) {
        return null;
      }

      eventBus.chime(deletedMessage);
      if (onSuccess) {
        await onSuccess();
      }
      if (boundFallbackRoute) {
        await router.push(boundFallbackRoute);
      }
      return true;
    });
  }

  if (syncLoadIndexOnMount) {
    if (resourceBinding === undefined || targetBinding === undefined) {
      throw new Error('useApi: `syncLoadIndexOnMount` requires `resource` and `target`');
    }
    if (getCurrentInstance()) {
      onMounted(() => {
        void loadResourceIndex();
      });
    }
  }

  if (syncLoadWithRouteId) {
    if (!routeIdParam) {
      throw new Error('useApi: `syncLoadWithRouteId` requires `routeIdParam`');
    }
    if (getCurrentInstance()) {
      onMounted(() => {
        void loadResource();
      });
      watch(
        () => route.params[routeIdParam],
        (routeIdValue) => {
          if (!routeIdValue) {
            return;
          }
          void loadResource();
        },
      );
    }
  }

  return {
    api,
    createResource,
    destroyResource,
    loadResource,
    loadResourceIndex,
    updateResource,
    scopedApi,
    ...(indexLoading ? { indexLoading } : {}),
  };
}
